<template>
  <div class="ai-news-ticker-white">
    <div class="ticker-wrapper">
      <!-- Left Section: Label Badge -->
      <div class="ticker-label">
        <div class="label-badge">
          <i class="fas fa-bolt"></i>
          <span>AI NEWS</span>
        </div>
      </div>

      <!-- Center: Scrolling News Display -->
      <div class="ticker-content-area">
        <div class="ticker-scroll-track">
          <div
            v-for="(item, index) in [...newsItems, ...newsItems]"
            :key="index"
            class="news-item"
          >
            <span class="news-dot"></span>
            <span class="news-text">{{ item.title }}</span>
            <span class="news-time">{{ item.timestamp }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface NewsItem {
  icon: string
  title: string
  timestamp: string
}

const newsItems = ref<NewsItem[]>([
  {
    icon: '🤖',
    title: 'OpenAI announces GPT-5 with revolutionary reasoning capabilities',
    timestamp: '2h ago'
  },
  {
    icon: '⚡',
    title: 'Google Gemini 2.0 surpasses GPT-4 in coding benchmarks',
    timestamp: '4h ago'
  },
  {
    icon: '🧠',
    title: 'DeepMind achieves breakthrough in protein folding with AlphaFold 3',
    timestamp: '6h ago'
  },
  {
    icon: '🚀',
    title: 'Microsoft integrates advanced AI into Office suite with Copilot Pro',
    timestamp: '8h ago'
  },
  {
    icon: '💡',
    title: 'Meta releases Llama 3 - open source model rivaling proprietary AI',
    timestamp: '12h ago'
  }
])
</script>

<style scoped>
/* Clean White News Ticker */
.ai-news-ticker-white {
  width: 100vw;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  margin: 0;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 85;
  border-bottom: 1px solid #e5e7eb;
}

.ticker-wrapper {
  max-width: 1800px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  height: 42px;
  position: relative;
  z-index: 10;
  padding: 0 24px;
  gap: 20px;
}

/* Left Label Section */
.ticker-label {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.label-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 6px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-family: 'Inter', sans-serif;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.label-badge i {
  font-size: 10px;
}

/* Scrolling Content Area */
.ticker-content-area {
  flex: 1;
  overflow: hidden;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
}

.ticker-content-area::before,
.ticker-content-area::after {
  content: '';
  position: absolute;
  top: 0;
  height: 100%;
  width: 40px;
  z-index: 2;
  pointer-events: none;
}

.ticker-content-area::before {
  left: 0;
  background: linear-gradient(to right, #ffffff, transparent);
}

.ticker-content-area::after {
  right: 0;
  background: linear-gradient(to left, #ffffff, transparent);
}

.ticker-scroll-track {
  display: flex;
  gap: 32px;
  animation: scroll-horizontal 45s linear infinite;
  will-change: transform;
}

@keyframes scroll-horizontal {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.ticker-content-area:hover .ticker-scroll-track {
  animation-play-state: paused;
}

.news-item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-width: 320px;
  padding: 4px 0;
  cursor: default;
}

.news-dot {
  width: 6px;
  height: 6px;
  background: #3b82f6;
  border-radius: 50%;
  flex-shrink: 0;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}

.news-text {
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-family: 'Inter', sans-serif;
  transition: color 0.2s ease;
}

.news-time {
  color: #9ca3af;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  padding: 2px 8px;
  background: #f3f4f6;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .ai-news-ticker-white {
    top: 60px;
  }

  .ticker-wrapper {
    height: 38px;
    padding: 0 16px;
    gap: 12px;
  }

  .label-badge {
    padding: 5px 10px;
    font-size: 10px;
  }

  .ticker-scroll-track {
    gap: 24px;
    animation-duration: 35s;
  }

  .news-item {
    min-width: 280px;
  }

  .news-text {
    font-size: 12px;
  }

  .news-time {
    font-size: 10px;
    padding: 2px 6px;
  }
}

@media (max-width: 480px) {
  .ai-news-ticker-white {
    top: 60px;
  }

  .ticker-wrapper {
    height: 34px;
    padding: 0 12px;
    gap: 8px;
  }

  .label-badge span {
    display: none;
  }

  .label-badge {
    padding: 6px 8px;
  }

  .ticker-scroll-track {
    gap: 20px;
    animation-duration: 30s;
  }

  .news-item {
    min-width: 240px;
  }

  .news-text {
    font-size: 11px;
  }

  .news-time {
    display: none;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .ticker-scroll-track {
    animation-duration: 90s;
  }

  .news-dot {
    animation: none;
  }
}
</style>
